import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Contact";
import { PageHero } from "@/components/site/PageHero";

export default function Pricing() {
  const [packages, setPackages] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Pricing | LaunchLab";
    api.get("/pricing").then((r) => setPackages(r.data)).catch(() => setPackages([]));
  }, []);

  return (
    <div data-testid="pricing-page" className="relative w-full max-w-[100vw] overflow-x-clip text-slate-100 min-h-screen">
      <Header />
      <PageHero
        testid="pricing-hero"
        kicker="Engagement Models"
        title="CHOOSE YOUR"
        highlight="LAUNCH PATH."
        subtext="Every venture is different. Packages are tailored to your stage — talk to us for a customised proposal."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {packages === null ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
        ) : packages.length === 0 ? (
          <div className="glass rounded-2xl p-14 text-center text-slate-400" data-testid="pricing-empty">Pricing packages will be published soon.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-testid="pricing-grid">
            {packages.map((p, i) => {
              const featured = i === 1;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`card-beam relative rounded-2xl p-7 sm:p-8 flex flex-col transition-all duration-300 hover:-translate-y-2 ${
                    featured
                      ? "border-2 border-[#FF8800]/60 bg-[#0D111D] shadow-[0_0_60px_rgba(255,136,0,0.12)]"
                      : "border border-slate-800 bg-[#0D111D]/90"
                  }`}
                >
                  {featured && (
                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-mono-tech text-[10px] tracking-[0.2em] font-semibold">
                      MOST POPULAR
                    </span>
                  )}
                  <h3 className="font-display text-2xl font-extrabold tracking-wide text-white">{p.name}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.description}</p>
                  <div className="mt-6">
                    {p.price ? (
                      <div className="flex items-baseline gap-2">
                        <span className="font-display text-4xl font-extrabold text-gradient-saffron">{p.price}</span>
                        <span className="text-xs text-slate-500 font-mono-tech">{p.billing_type}</span>
                      </div>
                    ) : (
                      <span className="font-display text-2xl font-bold text-white">Contact Us</span>
                    )}
                  </div>
                  <ul className="mt-7 space-y-3 flex-1" data-testid={`pricing-features-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                    {(p.features || "").split("\n").filter(Boolean).map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-slate-200">
                        <Check className="w-4 h-4 text-[#00E676] mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`group mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm tracking-wide transition-transform duration-300 hover:scale-[1.03] ${
                      featured
                        ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]"
                        : "glass text-slate-100 hover:border-[#FF8800]/50"
                    }`}
                  >
                    {p.cta_label || "Get Started"}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
